import L from 'leaflet';
import { MutableRefObject, Dispatch, SetStateAction } from 'react';
import currentLocationIcon from '../../../static/images/icon_current_location.png';
import axios, { AxiosResponse, AxiosError } from 'axios';
import eventBus, { EVENT_NAMES } from '../../../utils/event_bus';

// 天地图密钥
export const TIANDITU_KEY = 'fff964e29e99943e488215b6ef39a11a';

export interface LocationInfo {
  latlng: L.LatLng;
  address: string;
  /** 位置标签，例如「发货地」「当前位置」 */
  label?: string;
}

export interface MapOriginLocation {
  lat: number;
  lon: number;
  address: string;
  label?: string;
}

export interface SearchResult {
  name: string;
  address: string;
  location: L.LatLng;
}

/**
 * 司机信息接口
 */
export interface DriverInfo {
  _id: string;
  name: string;
  phone: string;
  lastPosition: {
    lat: number;
    lon: number;
  };
  updatedAt: string;
}

/**
 * 地图标记点信息接口
 */
export interface MapMarkerInfo {
  // 经纬度
  latlng: L.LatLng;
  // 标记的标题
  title: string;
  // 标记的内容
  content: string;
  // 标记的类型，可用于区分不同样式的标记
  type: string;
  // 可选的唯一标识符
  id?: string;
}

/**
 * 初始化地图
 */
export const initializeMap = (
  mapContainerRef: MutableRefObject<HTMLDivElement | null>,
  mapInstanceRef: MutableRefObject<L.Map | null>,
  center: [number, number],
  zoom: number,
  onMapReady?: (map: L.Map) => void,
  handleLocateMe?: () => void
): (() => void) | undefined => {
  if (!mapContainerRef.current) return;
  
  // 如果地图已经初始化，则不再重复初始化
  if (mapInstanceRef.current) return;

  // 创建地图实例
  const mapInstance = L.map(mapContainerRef.current, {
    center,
    zoom,
    minZoom: 2,
    maxZoom: 18,
    zoomControl: true,
    attributionControl: true
  });

  // 添加天地图矢量图层
  const vectorLayer = L.tileLayer(
    `https://t{s}.tianditu.gov.cn/vec_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=vec&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${TIANDITU_KEY}`,
    {
      subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
      attribution: '天地图'
    }
  );

  // 添加天地图标注图层
  const labelLayer = L.tileLayer(
    `https://t{s}.tianditu.gov.cn/cva_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cva&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${TIANDITU_KEY}`,
    {
      subdomains: ['0', '1', '2', '3', '4', '5', '6', '7']
    }
  );

  // 添加图层到地图
  vectorLayer.addTo(mapInstance);
  labelLayer.addTo(mapInstance);

  // 保存地图实例
  mapInstanceRef.current = mapInstance;
  
  // 添加地图点击事件处理
  mapInstance.on('click', async (e) => {
    const { lat, lng } = e.latlng;
    
    // 移动地图到点击位置
    mapInstance.setView([lat, lng], mapInstance.getZoom());
    
    // 获取地址信息
    try {
      const response = await fetch(`https://api.tianditu.gov.cn/geocoder?postStr={"lon":${lng},"lat":${lat},"ver":1}&type=geocode&tk=${TIANDITU_KEY}`);
      const data = await response.json();
      
      if (data.status === '0') {
        const address = data.result.formatted_address || '未知地址';
        
        // 发布位置变更事件
        eventBus.publish(EVENT_NAMES.MAP_LOCATION_CHANGE, lat, lng, address);
      }
    } catch (error) {
      console.error('获取地址信息失败:', error);
      // 即使获取地址失败，也发布位置变更事件，使用默认地址
      eventBus.publish(EVENT_NAMES.MAP_LOCATION_CHANGE, lat, lng, '获取地址失败');
    }
  });
  
  // 如果提供了handleLocateMe函数，则添加定位按钮
  if (handleLocateMe) {
    addLocateControl(mapInstance, handleLocateMe);
  }

  // 如果提供了onMapReady回调，则调用
  if (onMapReady) {
    onMapReady(mapInstance);
  }

  // 组件卸载时清理地图
  return () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
  };
};

/**
 * 添加定位控件到地图
 */
export const addLocateControl = (mapInstance: L.Map, handleLocateMe: () => void): void => {
  const locateControl = L.Control.extend({
    options: {
      position: 'topleft'
    },
    
    onAdd: function() {
      const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
      const button = L.DomUtil.create('a', 'leaflet-control-locate', container);
      
      button.href = '#';
      button.title = '定位到我的位置';
      button.style.backgroundImage = `url(${currentLocationIcon})`;
      button.style.backgroundSize = '20px 20px';
      button.style.backgroundPosition = 'center';
      button.style.backgroundRepeat = 'no-repeat';
      button.style.width = '30px';
      button.style.height = '30px';
      button.style.display = 'block';
      
      L.DomEvent.on(button, 'click', function(e) {
        L.DomEvent.stopPropagation(e);
        L.DomEvent.preventDefault(e);
        handleLocateMe();
      });
      
      return container;
    }
  });
  
  // 添加定位按钮到地图
  new locateControl().addTo(mapInstance);
};

/**
 * 获取当前位置并在地图上标记
 */
export const locateCurrentPosition = (
  mapInstanceRef: MutableRefObject<L.Map | null>,
  locationMarkerRef: MutableRefObject<L.CircleMarker | null>,
  locationCircleRef: MutableRefObject<L.Circle | null>,
  setCurrentLocation: (location: LocationInfo) => void,
  setShowLocationInfo: (show: boolean) => void,
  onLocationChange?: (latitude: number, longitude: number, address: string) => void
): void => {
  if (!mapInstanceRef.current) return;
  
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const latlng = L.latLng(latitude, longitude);
        
        // 移动地图到当前位置
        mapInstanceRef.current?.setView(latlng, 15);
        
        // 清除之前的标记
        if (locationMarkerRef.current) {
          locationMarkerRef.current.remove();
        }
        if (locationCircleRef.current) {
          locationCircleRef.current.remove();
        }
        
        // 添加当前位置标记
        if (mapInstanceRef.current) {
          locationMarkerRef.current = L.circleMarker(latlng, {
            radius: 8,
            fillColor: '#1976d2',
            color: '#fff',
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8
          }).addTo(mapInstanceRef.current);
          
          // 添加200米半径圆
          locationCircleRef.current = L.circle(latlng, {
            radius: 200,
            fillColor: '#1976d2',
            fillOpacity: 0.1,
            color: '#1976d2',
            weight: 1
          }).addTo(mapInstanceRef.current);
        }
        
        // 获取地址信息
        fetch(`https://api.tianditu.gov.cn/geocoder?postStr={"lon":${longitude},"lat":${latitude},"ver":1}&type=geocode&tk=${TIANDITU_KEY}`)
          .then(response => response.json())
          .then(data => {
            if (data.status === '0') {
              const address = data.result.formatted_address || '未知地址';
              setCurrentLocation({ latlng, address });
              setShowLocationInfo(true);
              
              // 兼容旧的回调方式
              if (onLocationChange) {
                onLocationChange(latitude, longitude, address);
              }
              
              // 发布位置变更事件
              eventBus.publish(EVENT_NAMES.MAP_LOCATION_CHANGE, latitude, longitude, address);
            }
          })
          .catch(error => {
            console.error('获取地址信息失败:', error);
            const errorAddress = '获取地址失败';
            setCurrentLocation({ latlng, address: errorAddress });
            setShowLocationInfo(true);
            
            // 兼容旧的回调方式
            if (onLocationChange) {
              onLocationChange(latitude, longitude, errorAddress);
            }
            
            // 即使获取地址失败，也发布位置变更事件
            eventBus.publish(EVENT_NAMES.MAP_LOCATION_CHANGE, latitude, longitude, errorAddress);
          });
      },
      (error) => {
        console.error('获取位置失败:', error);
        alert('无法获取您的位置，请确保已授予位置权限。');
      }
    );
  } else {
    alert('您的浏览器不支持地理定位功能。');
  }
};

/**
 * 搜索地址
 */
export const searchAddress = (
  keyword: string,
  setSearchResults: Dispatch<SetStateAction<SearchResult[]>>,
  setIsLoading: Dispatch<SetStateAction<boolean>>
): void => {
  if (!keyword.trim()) {
    setSearchResults([]);
    return;
  }
  
  setIsLoading(true);
  
  // 使用天地图地址搜索API
  fetch(`https://api.tianditu.gov.cn/v2/search?postStr={"keyWord":"${encodeURIComponent(keyword)}","level":"11","mapBound":"73.66,3.86,135.05,53.55","queryType":"1","start":"0","count":"20"}&type=query&tk=${TIANDITU_KEY}`)
    .then(response => response.json())
    .then(data => {
      setIsLoading(false);
      if (data.resultType === 1 && data.pois && data.pois.length > 0) {
        // 最多显示5个结果
        const results = data.pois.map((poi: any) => {
          // 解析经纬度字符串 "113.994606,22.525230" 格式为 [经度,纬度]
          const [longitude, latitude] = poi.lonlat.split(',').map(Number);
          return {
            name: poi.name,
            address: poi.address || '无详细地址',
            location: L.latLng(latitude, longitude) // 注意：纬度在前，经度在后
          };
        });
        setSearchResults(results);
      } else {
        setSearchResults([]);
      }
    })
    .catch(error => {
      console.error('地址搜索失败:', error);
      setIsLoading(false);
      setSearchResults([]);
    });
};

/**
 * 显示搜索结果位置
 */
export const showSearchResultOnMap = (
  mapInstanceRef: MutableRefObject<L.Map | null>,
  locationMarkerRef: MutableRefObject<L.CircleMarker | null>,
  locationCircleRef: MutableRefObject<L.Circle | null>,
  result: SearchResult,
  setCurrentLocation: (location: LocationInfo) => void,
  setShowLocationInfo: (show: boolean) => void,
  onLocationChange?: (latitude: number, longitude: number, address: string) => void
): void => {
  if (!mapInstanceRef.current) return;
  
  const { location, address } = result;
  
  // 移动地图到搜索结果位置
  mapInstanceRef.current.setView(location, 15);
  
  // 清除之前的标记
  if (locationMarkerRef.current) {
    locationMarkerRef.current.remove();
  }
  if (locationCircleRef.current) {
    locationCircleRef.current.remove();
  }
  
  // 添加位置标记
  if (mapInstanceRef.current) {
    locationMarkerRef.current = L.circleMarker(location, {
      radius: 8,
      fillColor: '#1976d2',
      color: '#fff',
      weight: 2,
      opacity: 1,
      fillOpacity: 0.8
    }).addTo(mapInstanceRef.current);
    
    // 添加200米半径圆
    locationCircleRef.current = L.circle(location, {
      radius: 200,
      fillColor: '#1976d2',
      fillOpacity: 0.1,
      color: '#1976d2',
      weight: 1
    }).addTo(mapInstanceRef.current);
  }
  
  // 设置位置信息
  setCurrentLocation({ latlng: location, address: result.name + ' - ' + address });
  setShowLocationInfo(true);
  
  // 兼容旧的回调方式
  if (onLocationChange) {
    onLocationChange(location.lat, location.lng, result.name + ' - ' + address);
  }
  
  // 发布位置变更事件
  eventBus.publish(EVENT_NAMES.MAP_LOCATION_CHANGE, location.lat, location.lng, result.name + ' - ' + address);
};

/**
 * 更新地图标记点
 * @param mapInstanceRef 地图实例引用
 * @param markersRef 标记点引用集合
 * @param markers 标记点信息数组
 */
export const updateMarkers = (
  mapInstanceRef: MutableRefObject<L.Map | null>,
  markersRef: MutableRefObject<Map<string, L.Marker>>,
  markers: MapMarkerInfo[]
): void => {
  if (!mapInstanceRef.current) return;
  
  const map = mapInstanceRef.current;
  const existingMarkers = markersRef.current;
  
  // 创建一个新的标记点集合，用于跟踪当前更新中的标记点
  const updatedMarkerIds = new Set<string>();
  
  // 添加或更新标记点
  markers.forEach(marker => {
    const markerId = marker.id || `${marker.latlng.lat}-${marker.latlng.lng}-${marker.type}`;
    updatedMarkerIds.add(markerId);
    
    // 如果标记点已存在，则更新其位置和弹出内容
    if (existingMarkers.has(markerId)) {
      const existingMarker = existingMarkers.get(markerId)!;
      existingMarker.setLatLng(marker.latlng);
      existingMarker.setPopupContent(`<div><strong>${marker.title}</strong><p>${marker.content}</p></div>`);
    } else {
      // 创建新的标记点
      const newMarker = L.marker(marker.latlng).addTo(map);
      
      // 根据标记类型设置不同的图标样式
      if (marker.type) {
        // 这里可以根据type设置不同的图标
        // 例如：newMarker.setIcon(getIconByType(marker.type));
      }
      
      // 添加弹出信息
      newMarker.bindPopup(`<div><strong>${marker.title}</strong><p>${marker.content}</p></div>`);
      
      // 将新标记点添加到集合中
      existingMarkers.set(markerId, newMarker);
    }
  });
  
  // 移除不再需要的标记点
  existingMarkers.forEach((marker, id) => {
    if (!updatedMarkerIds.has(id)) {
      marker.remove();
      existingMarkers.delete(id);
    }
  });
  
  // 如果有标记点，自动调整地图视图以显示所有标记点
  if (markers.length > 0) {
    const bounds = L.latLngBounds(markers.map(m => m.latlng));
    map.fitBounds(bounds, { padding: [50, 50] });
  }
};